import { useEffect, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Pencil } from "lucide-react";
import client from "@/lib/api";
import { SectionHeader, EmptyState, notifyError, notifyOk, MLFields, toML, locRu } from "./_ui";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Questionnaire() {
  const [groups, setGroups] = useState([]);
  const [options, setOptions] = useState([]);
  const [dlg, setDlg] = useState(null); // {kind:'group'|'option', mode:'new'|'edit', ...}

  const load = async () => {
    try {
      const [g, o] = await Promise.all([client.get("/admin/option-groups"), client.get("/admin/options")]);
      setGroups(g.data); setOptions(o.data);
    } catch (e) { notifyError(e); }
  };
  useEffect(() => { load(); }, []);

  const groupOptions = (gid) => options.filter((o) => o.group_id === gid).sort((a, b) => a.order - b.order);

  const save = async () => {
    try {
      if (dlg.kind === "group") {
        const body = { title: dlg.value, order: dlg.order ?? groups.length + 1, active: dlg.active !== false };
        if (dlg.mode === "edit") await client.put(`/admin/option-groups/${dlg.id}`, body);
        else await client.post("/admin/option-groups", body);
      } else {
        const body = { group_id: dlg.group_id, label: dlg.value, order: dlg.order ?? groupOptions(dlg.group_id).length + 1, active: dlg.active !== false, always_on: !!dlg.always_on };
        if (dlg.mode === "edit") await client.put(`/admin/options/${dlg.id}`, body);
        else await client.post("/admin/options", body);
      }
      setDlg(null); notifyOk("Сохранено"); load();
    } catch (e) { notifyError(e); }
  };

  const toggleGroup = async (g) => { await client.put(`/admin/option-groups/${g.id}`, { ...g, active: !g.active }); load(); };
  const delGroup = async (g) => { if (window.confirm("Удалить группу и все её опции?")) { await client.delete(`/admin/option-groups/${g.id}`); load(); } };
  const moveGroup = async (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= groups.length) return;
    const a = groups[idx], b = groups[j];
    await client.post("/admin/option-groups/reorder", { items: [{ id: a.id, order: b.order }, { id: b.id, order: a.order }] }); load();
  };
  const toggleOption = async (o) => { await client.put(`/admin/options/${o.id}`, { ...o, active: !o.active }); load(); };
  const toggleAlwaysOn = async (o) => { await client.put(`/admin/options/${o.id}`, { ...o, always_on: !o.always_on }); load(); };
  const delOption = async (o) => { if (window.confirm("Удалить опцию?")) { await client.delete(`/admin/options/${o.id}`); load(); } };
  const moveOption = async (gid, idx, dir) => {
    const list = groupOptions(gid); const j = idx + dir; if (j < 0 || j >= list.length) return;
    const a = list[idx], b = list[j];
    await client.post("/admin/options/reorder", { items: [{ id: a.id, order: b.order }, { id: b.id, order: a.order }] }); load();
  };

  return (
    <div>
      <SectionHeader
        title="Опросник комплектации"
        description="Группы и опции. Каждое название хранится на 4 языках (DE/EN/PL/RU). Боту показывается RU, в акт печатается язык акта."
        action={
          <Button data-testid="group-add-btn" onClick={() => setDlg({ kind: "group", mode: "new", value: toML(""), active: true })} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
            <Plus className="w-4 h-4 mr-1.5" /> Группа
          </Button>
        }
      />

      {groups.length === 0 ? (
        <EmptyState text="Опросник пуст." />
      ) : (
        <div className="space-y-4">
          {groups.map((g, gi) => (
            <div key={g.id} data-testid={`group-${g.id}`} className="border border-neutral-200 rounded-lg bg-white">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-100">
                <div className="flex flex-col">
                  <button onClick={() => moveGroup(gi, -1)} className="text-neutral-400 hover:text-black"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => moveGroup(gi, 1)} className="text-neutral-400 hover:text-black"><ChevronDown className="w-3.5 h-3.5" /></button>
                </div>
                <span className={`font-semibold flex-1 ${g.active ? "" : "text-neutral-400 line-through"}`}>{locRu(g.title)}</span>
                <Switch data-testid={`group-active-${g.id}`} checked={g.active} onCheckedChange={() => toggleGroup(g)} />
                <button onClick={() => setDlg({ kind: "group", mode: "edit", id: g.id, value: toML(g.title), order: g.order, active: g.active })} data-testid={`group-edit-${g.id}`} className="p-1.5 text-neutral-600 hover:text-black"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => delGroup(g)} data-testid={`group-delete-${g.id}`} className="p-1.5 text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="divide-y divide-neutral-100">
                {groupOptions(g.id).map((o, oi) => (
                  <div key={o.id} data-testid={`option-${o.id}`} className="flex items-center gap-2 px-4 py-2 text-sm">
                    <div className="flex flex-col">
                      <button onClick={() => moveOption(g.id, oi, -1)} className="text-neutral-300 hover:text-black"><ChevronUp className="w-3 h-3" /></button>
                      <button onClick={() => moveOption(g.id, oi, 1)} className="text-neutral-300 hover:text-black"><ChevronDown className="w-3 h-3" /></button>
                    </div>
                    <span className={`flex-1 ${o.active ? "" : "text-neutral-400 line-through"}`}>
                      {locRu(o.label)}
                      {o.always_on && (
                        <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid={`option-alwayson-badge-${o.id}`}>всегда в акте</span>
                      )}
                    </span>
                    <label className="flex items-center gap-1 text-xs text-neutral-500 mr-1" title="Всегда включено — в боте не спрашивается, добавляется в каждый акт">
                      <Switch data-testid={`option-alwayson-${o.id}`} checked={!!o.always_on} onCheckedChange={() => toggleAlwaysOn(o)} />
                      всегда
                    </label>
                    <Switch data-testid={`option-active-${o.id}`} checked={o.active} onCheckedChange={() => toggleOption(o)} />
                    <button onClick={() => setDlg({ kind: "option", mode: "edit", id: o.id, group_id: o.group_id, value: toML(o.label), order: o.order, active: o.active, always_on: o.always_on })} className="p-1 text-neutral-500 hover:text-black"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => delOption(o)} className="p-1 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="px-4 py-3 border-t border-neutral-100">
                <Button size="sm" variant="outline" onClick={() => setDlg({ kind: "option", mode: "new", group_id: g.id, value: toML(""), active: true })} data-testid={`option-add-${g.id}`}>
                  <Plus className="w-4 h-4 mr-1" /> Опция
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{dlg?.kind === "group" ? "Группа" : "Опция"} ({dlg?.mode === "edit" ? "редактирование" : "новая"})</DialogTitle>
          </DialogHeader>
          {dlg && (
            <MLFields value={dlg.value} onChange={(v) => setDlg({ ...dlg, value: v })} testid="ml" label="Название на 4 языках" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Отмена</Button>
            <Button data-testid="ml-save-btn" onClick={save} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
